// Reine Rechnung hinter jeder eigenen Reihenfolge: Tabellen-Spalten
// (column-order.ts) und Karten (anordnung.svelte.ts). Gespeicherte
// Reihenfolge anwenden, eine Karte verschieben, daraus die CSS-order-Werte
// der Kinder eines Behälters machen. Ohne Svelte, damit sie testbar ist
// (anordnung.test.mjs). Bis 16.10.2026 hieß der Spalten-Teil
// spalten-ordnung.ts.

/** Die gespeicherte Reihenfolge eines Nutzers auf die Dinge anwenden, die
 *  es gerade gibt (`standard` = Reihenfolge im Code oder im Markup).
 *
 *  Neue Dinge -- die es in der gespeicherten Reihenfolge noch nicht gibt
 *  -- kommen hinter ihren Vorgänger aus der Standard-Reihenfolge. Bis
 *  25.09.2026 landeten Spalten am Ende: „Nächster" stand auf der
 *  Aufgaben-Seite hinter „Aktion", weil Tims Reihenfolge von vorher
 *  gespeichert war. Wer etwas einfügt, meint einen Platz; das Ende ist
 *  keiner. */
export function ordne<T extends { id: string }>(
  gespeichert: readonly string[] | null | undefined,
  standard: readonly T[],
): T[] {
  if (!gespeichert || gespeichert.length === 0) return [...standard];
  const nachId = new Map(standard.map((s) => [s.id, s]));
  // Bekannte in der Reihenfolge des Nutzers; entfernte fallen weg.
  const aus: T[] = gespeichert.filter((id) => nachId.has(id)).map((id) => nachId.get(id)!);
  // Neue in Standard-Reihenfolge einsetzen, je hinter den nächsten
  // Vorgänger, der schon steht -- so behalten auch mehrere neue ihre
  // Reihenfolge untereinander.
  standard.forEach((s, i) => {
    if (aus.includes(s)) return;
    let pos = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = aus.indexOf(standard[j]);
      if (k >= 0) {
        pos = k + 1;
        break;
      }
    }
    aus.splice(pos, 0, s);
  });
  return aus;
}

/** Schiebt `von` an den Platz von `nach` (Einfüge-Modell) und gibt die
 *  neue, zu speichernde Reihenfolge zurück; null, wenn sich nichts
 *  ändert. `aktuell` = die Karten, die da sind, in ihrer jetzigen
 *  Reihenfolge.
 *
 *  Karten, die gespeichert, aber gerade nicht da sind (der Drucker ist
 *  aus, also fehlt „Werte"), behalten ihren Platz hinter ihrem
 *  gespeicherten Vorgänger. Sonst verlöre eine Seite die eigene Ordnung
 *  jedes Mal, wenn man sie in einem anderen Zustand umsortiert. */
export function verschiebe(
  gespeichert: readonly string[] | null | undefined,
  aktuell: readonly string[],
  von: string,
  nach: string,
): string[] | null {
  const neu = [...aktuell];
  const vi = neu.indexOf(von);
  const ni = neu.indexOf(nach);
  if (vi < 0 || ni < 0 || vi === ni) return null;
  neu.splice(vi, 1);
  neu.splice(ni, 0, von);
  const alt = gespeichert ?? [];
  alt.forEach((id, i) => {
    if (neu.includes(id)) return;
    let pos = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = neu.indexOf(alt[j]);
      if (k >= 0) {
        pos = k + 1;
        break;
      }
    }
    neu.splice(pos, 0, id);
  });
  return neu;
}

/** CSS-order je Kind eines Behälters. `kinder` in Markup-Reihenfolge: je
 *  die Kennung einer Karte oder null für alles andere (Reiter, Hinweise,
 *  Fußzeilen). `rang` = Platz jeder Karte in der Anordnung.
 *
 *  Karten bekommen 1 + Rang. Was keine Karte ist, bleibt, wo es steht:
 *  vor der ersten Karte oben (0), nach der letzten unten, dazwischen
 *  hängt es an der Karte davor (gleicher Wert; bei Gleichstand gilt im
 *  Browser die Markup-Reihenfolge, es folgt ihr also direkt). */
export function ordnungsWerte(kinder: readonly (string | null)[], rang: ReadonlyMap<string, number>): number[] {
  const istKarte = (id: string | null): id is string => id !== null && rang.has(id);
  let letzte = -1;
  let max = 0;
  kinder.forEach((id, i) => {
    if (!istKarte(id)) return;
    letzte = i;
    max = Math.max(max, rang.get(id)! + 1);
  });
  let davor = 0;
  return kinder.map((id, i) => {
    if (istKarte(id)) {
      davor = rang.get(id)! + 1;
      return davor;
    }
    return letzte >= 0 && i > letzte ? max + 1 : davor;
  });
}
